<template>
  <div class="ad-revenue-query">
    <!-- KPI 单独一行 -->
    <el-card shadow="never" class="kpi-panel animate__animated animate__fadeInUp">
      <div class="summary-cards">
        <el-card
          class="summary-card card-total"
          :style="kpiCardStyle(kpiBgTotal, kpiAccentTotal)"
        >
          <div class="summary-content">
            <span class="summary-icon-wrap" :style="{ backgroundColor: kpiAccentTotal }">
              <i class="el-icon-wallet summary-icon"></i>
            </span>
            <div class="summary-value">{{ totalEcpmDisplay }}</div>
            <div class="summary-label">总收益</div>
          </div>
        </el-card>
        <el-card
          v-for="(item, index) in adnSummaryList"
          :key="item.adnName"
          class="summary-card"
          :style="kpiCardStyle(kpiBgAdn[index % kpiBgAdn.length], kpiAccentAdn[index % kpiAccentAdn.length])"
        >
          <div class="summary-content">
            <span
              class="summary-icon-wrap"
              :style="{ backgroundColor: kpiAccentAdn[index % kpiAccentAdn.length] }"
            >
              <i class="el-icon-s-data summary-icon"></i>
            </span>
            <div class="summary-value">{{ item.totalEcpm }}</div>
            <div class="summary-label">{{ item.adnName }}收益</div>
          </div>
        </el-card>
        <el-card
          class="summary-card card-count"
          :style="kpiCardStyle(kpiBgCount, kpiAccentCount)"
        >
          <div class="summary-content">
            <span class="summary-icon-wrap" :style="{ backgroundColor: kpiAccentCount }">
              <i class="el-icon-document summary-icon"></i>
            </span>
            <div class="summary-value">{{ totalRecordCount }}</div>
            <div class="summary-label">总记录数</div>
          </div>
        </el-card>
      </div>
    </el-card>

    <!-- 筛选 + 表格 -->
    <el-card shadow="never" class="table-panel animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model.trim="form.userId"
              clearable
              placeholder="用户ID"
              class="filter-input-sm"
            />
            <el-select v-model="form.adnName" clearable filterable placeholder="全部平台" class="filter-select">
              <el-option
                v-for="item in adnOptions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <el-select v-model="form.adType" clearable filterable placeholder="全部类型" class="filter-select">
              <el-option
                v-for="item in adTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-input
              v-if="form.ecpmMode === 'exact'"
              v-model.trim="form.ecpmExact"
              clearable
              placeholder="收入精确值"
              class="filter-input-sm"
            />
            <template v-if="form.ecpmMode === 'range'">
              <el-input v-model.trim="form.ecpmMin" clearable placeholder="收入下限" class="filter-input-xs" />
              <el-input v-model.trim="form.ecpmMax" clearable placeholder="收入上限" class="filter-input-xs" />
            </template>
            <el-date-picker
              v-model="form.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :default-time="['00:00:00', '23:59:59']"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              :clearable="false"
              class="filter-daterange"
            />
            <date-range-quick v-model="form.dateRange" @change="handleSearch" />
            <el-button type="primary" :loading="loading" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">重置</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && !hasTable && pagination.total === 0"
        description="所选时间范围内暂无广告记录，请扩大时间范围后重试"
      />

      <template v-else>
        <div class="table-scroll">
          <el-table
            :data="tableData"
            border
            stripe
            height="100%"
            v-loading="loading"
            element-loading-text="正在查询..."
            class="animate__animated animate__fadeIn"
            row-key="id"
          >
            <el-table-column prop="id" label="ID" width="80" align="center"/>
            <el-table-column prop="userId" label="用户ID" width="90" align="center"/>
            <el-table-column prop="adnName" label="广告平台" min-width="110" :formatter="adnNameFormatter" />
            <el-table-column prop="adType" label="广告类型" min-width="100" :formatter="adTypeFormatter" />
            <el-table-column prop="ecpm" label="收入" min-width="100" :formatter="ecpmFormatter" />
            <el-table-column prop="transId" label="交易ID" min-width="160" show-overflow-tooltip />
            <el-table-column prop="reason" label="原因" min-width="100" show-overflow-tooltip />
            <el-table-column
              prop="createdTime"
              label="创建时间"
              min-width="170"
              :formatter="timeFormatter"
            />
          </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </template>
    </el-card>
  </div>
</template>

<script>
import { get } from '@/utils/http.js'
import { getAdPlatformSummary } from '@/api/dashboard'
import { getDefaultDateRange } from '@/utils/dateRange'
import { ecpmToYuan, yuanToEcpm } from '@/utils/coinMoney'
import DateRangeQuick from '@/components/DateRangeQuick.vue'
import kpiBgTotal from '@/assets/ad-revenue/kpi-total.png'
import kpiBgAdn0 from '@/assets/ad-revenue/kpi-adn-0.png'
import kpiBgAdn1 from '@/assets/ad-revenue/kpi-adn-1.png'
import kpiBgAdn2 from '@/assets/ad-revenue/kpi-adn-2.png'
import kpiBgAdn3 from '@/assets/ad-revenue/kpi-adn-3.png'
import kpiBgCount from '@/assets/ad-revenue/kpi-count.png'

const FIXED_ADN_OPTIONS = ['穿山甲', '快手', '腾讯广告', '百度联盟']

/** 后台原始 adnName → 页面展示名（旧数据可能是 network_46 / Pangle） */
const ADN_DISPLAY_MAP = {
  network_46: '穿山甲',
  Pangle: '穿山甲'
}

function normalizeAdnName(adnName) {
  if (!adnName) return adnName
  return ADN_DISPLAY_MAP[adnName] || adnName
}

function isAllowedAdn(adnName) {
  if (!adnName) return false
  return FIXED_ADN_OPTIONS.includes(normalizeAdnName(adnName))
}

function parsePageData(data) {
  if (!data) {
    return { records: [], total: 0, page: 1, pageSize: 10 }
  }
  const records = data.records || data.list || data.rows || []
  return {
    records: Array.isArray(records) ? records : [],
    total: Number(data.total ?? data.totalCount ?? 0),
    page: Number(data.page ?? data.current ?? data.pageNum ?? 1),
    pageSize: Number(data.pageSize ?? data.size ?? 10)
  }
}

export default {
  name: 'AdRevenueQuery',
  components: { DateRangeQuick },
  data() {
    return {
      form: {
        dateRange: getDefaultDateRange(),
        userId: '',
        adnName: '',
        adType: '',
        ecpmMode: '',
        ecpmExact: '',
        ecpmMin: '',
        ecpmMax: ''
      },
      adnOptions: [...FIXED_ADN_OPTIONS],
      adTypeOptions: [
        { label: 'banner', value: 'banner' },
        { label: '插屏', value: 'interstitial' },
        { label: '信息流', value: 'native_feed' },
        { label: '激励', value: 'reward' },
        { label: '开屏', value: 'splash' }
      ],
      loading: false,
      error: '',
      tableData: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      platformSummary: { totalRevenue: 0, totalCount: 0, items: [] },
      kpiBgTotal,
      kpiBgAdn: [kpiBgAdn0, kpiBgAdn1, kpiBgAdn2, kpiBgAdn3],
      kpiBgCount,
      kpiAccentTotal: '#1FADFF',
      kpiAccentAdn: ['#FFD61F', '#A3E01F', '#14CCCC', '#FF5C52'],
      kpiAccentCount: '#7033D6' // 总记录数：紫
    }
  },
  computed: {
    hasTable() {
      return this.pagination.total > 0 || (Array.isArray(this.tableData) && this.tableData.length > 0)
    },
    totalEcpmDisplay() {
      const revenue = this.platformSummary.totalRevenue || 0
      return ecpmToYuan(revenue).toFixed(3)
    },
    totalRecordCount() {
      return this.platformSummary.totalCount || 0
    },
    adnSummaryList() {
      const itemsMap = {}
      ;(this.platformSummary.items || []).forEach(item => {
        if (!item.adnName || !isAllowedAdn(item.adnName)) return
        const name = normalizeAdnName(item.adnName)
        const prev = itemsMap[name]
        if (prev) {
          itemsMap[name] = {
            adnName: name,
            ecpm: (prev.ecpm || 0) + (item.ecpm || 0),
            percent: (prev.percent || 0) + (item.percent || 0)
          }
        } else {
          itemsMap[name] = { ...item, adnName: name }
        }
      })
      return FIXED_ADN_OPTIONS.map(adnName => {
        const item = itemsMap[adnName]
        const ecpm = item ? (item.ecpm || 0) : 0
        return {
          adnName,
          totalEcpm: ecpmToYuan(Math.floor(ecpm)).toFixed(3),
          percent: item ? (item.percent || 0) : 0
        }
      })
    }
  },
  mounted() {
    this.handleSearch()
  },
  methods: {
    kpiCardStyle(bg, accent) {
      return {
        backgroundImage: `url(${bg})`,
        backgroundColor: 'transparent',
        border: 'none',
        color: '#1f2a44',
        '--kpi-accent': accent
      }
    },
    timeFormatter(row, column, cellValue) {
      if (!cellValue) return ''
      return String(cellValue).replace('T', ' ')
    },
    adnNameFormatter(row, column, cellValue) {
      return normalizeAdnName(cellValue) || ''
    },
    ecpmFormatter(row, column, cellValue) {
      return ecpmToYuan(cellValue || 0).toFixed(3)
    },
    adTypeFormatter(row, column, cellValue) {
      const map = {
        banner: 'banner',
        interstitial: '插屏',
        native_feed: '信息流',
        reward: '激励',
        splash: '开屏'
      }
      return map[cellValue] || cellValue || ''
    },

    /** 页面展示单位（元）转库内 eCPM（广告千分位，1元≈1000） */
    toEcpmUnit(displayVal) {
      if (displayVal === '' || displayVal == null) return null
      const n = Number(displayVal)
      if (Number.isNaN(n)) {
        throw new Error('收入筛选请输入有效数字')
      }
      return yuanToEcpm(n)
    },

    onEcpmModeChange() {
      this.form.ecpmExact = ''
      this.form.ecpmMin = ''
      this.form.ecpmMax = ''
    },

    appendEcpmParams(params) {
      if (this.form.ecpmMode === 'exact') {
        if (this.form.ecpmExact !== '') {
          params.ecpm = this.toEcpmUnit(this.form.ecpmExact)
        }
        return
      }
      if (this.form.ecpmMode === 'range') {
        if (this.form.ecpmMin !== '') params.ecpmMin = this.toEcpmUnit(this.form.ecpmMin)
        if (this.form.ecpmMax !== '') params.ecpmMax = this.toEcpmUnit(this.form.ecpmMax)
        if (params.ecpmMin != null && params.ecpmMax != null && params.ecpmMin > params.ecpmMax) {
          throw new Error('收入下限不能大于上限')
        }
      }
    },

    buildQueryParams(includePagination = true) {
      const [start, end] = this.form.dateRange || []
      if (!start || !end) {
        throw new Error('请选择时间范围')
      }
      if (new Date(end.replace(' ', 'T')) < new Date(start.replace(' ', 'T'))) {
        throw new Error('结束时间不能早于开始时间')
      }

      const params = {
        startTime: start,
        endTime: end
      }
      if (includePagination) {
        params.page = this.pagination.page
        params.pageSize = this.pagination.pageSize
      }
      if (this.form.userId) params.userId = Number(this.form.userId)
      if (this.form.adnName) params.adnName = this.form.adnName
      if (this.form.adType) params.adType = this.form.adType
      this.appendEcpmParams(params)
      return params
    },

    applyPageResult(data) {
      const page = parsePageData(data)
      // 列表展示全部广告平台，有啥显示啥；分页与返回条数一致
      this.tableData = page.records
      this.pagination.total = page.total
      this.pagination.pageSize = page.pageSize || this.pagination.pageSize
      this.pagination.page = page.page || this.pagination.page
      return page
    },

    /** 汇总卡片只保留四平台 */
    sanitizePlatformSummary(summary) {
      const raw = summary || { totalRevenue: 0, totalCount: 0, items: [] }
      const items = (raw.items || []).filter(item => isAllowedAdn(item.adnName))
      const totalRevenue = items.reduce((sum, item) => sum + (Number(item.ecpm) || 0), 0)
      return {
        totalRevenue: totalRevenue || raw.totalRevenue || 0,
        totalCount: raw.totalCount || 0,
        items: items.map(item => ({
          ...item,
          adnName: normalizeAdnName(item.adnName),
          percent: totalRevenue > 0 ? ((Number(item.ecpm) || 0) / totalRevenue) * 100 : 0
        }))
      }
    },

    async fetchPlatformSummary() {
      try {
        const [start, end] = this.form.dateRange || []
        if (!start || !end) return
        const params = { startTime: start, endTime: end }
        if (this.form.userId) params.userId = Number(this.form.userId)
        if (this.form.adnName) params.adnName = this.form.adnName
        if (this.form.adType) params.adType = this.form.adType
        this.appendEcpmParams(params)
        const summary = await getAdPlatformSummary(start, end, params)
        this.platformSummary = this.sanitizePlatformSummary(summary)
      } catch (e) {
        this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      }
    },

    handleReset() {
      this.form = {
        dateRange: getDefaultDateRange(),
        userId: '',
        adnName: '',
        adType: '',
        ecpmMode: '',
        ecpmExact: '',
        ecpmMin: '',
        ecpmMax: ''
      }
      this.tableData = []
      this.error = ''
      this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      this.pagination = { page: 1, pageSize: 10, total: 0 }
      this.handleSearch()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchData()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchData()
    },

    async handleSearch() {
      this.pagination.page = 1
      this.tableData = []
      this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      this.fetchPlatformSummary()
      await this.fetchData()
    },

    async fetchData() {
      this.error = ''
      this.loading = true

      try {
        const params = this.buildQueryParams(true)
        const result = await get('/admin/adRecord/page', params)
        this.applyPageResult(result && result.data)
      } catch (e) {
        this.error = e && e.message ? e.message : '请求出现错误'
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ad-revenue-query {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ad-revenue-query ::v-deep .el-table th,
.ad-revenue-query ::v-deep .el-table th.is-leaf {
  background-color: #D6E4FF;
}

.kpi-panel {
  flex-shrink: 0;
  margin-bottom: 16px;
  border-radius: 12px;
}

.kpi-panel ::v-deep .el-card__body {
  padding: 16px 18px;
}

.table-panel {
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-panel ::v-deep > .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.filter-card {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.filter-daterange {
  width: 380px;
}
.filter-input-sm {
  width: 140px;
}
.filter-input-xs {
  width: 110px;
}
.filter-select {
  width: 150px;
}

/* ===== 汇总卡片 ===== */
.summary-cards {
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
}

.summary-cards .summary-card {
  flex: 1;
  min-width: 0;
}

.summary-card {
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease;
  height: 100px;
  box-shadow: none;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
}

.summary-card ::v-deep .el-card__body {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 12px 10px;
}

.summary-card:hover {
  transform: translateY(-3px);
}

.summary-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.summary-icon {
  font-size: 18px;
  color: #fff;
  line-height: 1;
}

.summary-value {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 2px;
  color: #1f2a44;
}

.summary-label {
  font-size: 11px;
  color: #5a6b82;
  font-weight: normal;
}

.pagination-container {
  flex-shrink: 0;
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.el-card.animate__animated,
.el-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>